import React, { useState, useEffect } from 'react';
import { uniqueId } from 'lodash';
import { useToggle } from 'components/shared/hooks';
import { TEXT_VARIANTS, Text } from 'components/ui/Typography';
import { Spinner } from 'components/ui';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import useAutosize from 'components/shared/hooks/useAutosize';
import InfoTooltip from 'components/shared/InfoTooltip';
import Error from 'components/InPersonTraining/shared/Error';
import EditControls from './EditControls';
import ConfirmationModal from './ConfirmationModal';

const KeyTakeaways = ({ keyTakeaways, updateHandler, lessonId }) => {
  const [editMode, toggleEditMode] = useToggle(false);
  const [modalVisible, toggleModalVisible] = useToggle(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [keyTakeawaysText, setKeyTakeawaysText] = useState('');
  const autosizeRef = useAutosize();

  const DEFAULT_TEXT = t('lessons.recap.key_takeaways.default_text');

  useEffect(() => {
    setKeyTakeawaysText(keyTakeaways);
  }, [keyTakeaways]);

  const onCancelClick = () => {
    setError(false);
    setLoading(false);
    setKeyTakeawaysText(keyTakeaways);
    toggleEditMode(false);
  };

  const formatResponseText = content => content.replaceAll('- ', '');

  const getGPTResponse = () => {
    setError(false);
    setLoading(true);
    request
      .get(routes.LESSON_OVERVIEW.GENERATE_TAKEAWAYS(lessonId))
      .then(res => {
        const { keyTakeaways: generatedTakeaways } = res;
        const formattedTakeaways = formatResponseText(generatedTakeaways);
        setKeyTakeawaysText(formattedTakeaways);
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        setError(true);
        console.error(e);
      });
  };

  const discardAndGenerate = () => {
    setKeyTakeawaysText('');
    toggleModalVisible(false);
    getGPTResponse();
  };

  const generateTakeaways = () => {
    if (keyTakeawaysText) {
      toggleModalVisible(true);
    } else {
      getGPTResponse();
    }
  };

  const updateTakeaways = () => {
    updateHandler('key_takeaways', keyTakeawaysText);
    toggleEditMode(false);
    setError(false);
  };

  return (
    <>
      <section className="KeyTakeaways Module EditableModule f-1">
        <div className="Module-heading">
          <EditControls
            editMode={editMode}
            toggleEditMode={toggleEditMode}
            onCancelClick={onCancelClick}
            updateHandler={updateTakeaways}
            generateGPTResponse={generateTakeaways}
            readOnly={loading}
          />
          <Text component="h3" variant={TEXT_VARIANTS.BODY} className="mb-0">
            {t('lessons.recap.key_takeaways.title')}
            <InfoTooltip
              className="ml-8"
              placement="right"
              tooltip={t('lessons.recap.key_takeaways.tooltip')}
            />
          </Text>
        </div>

        {editMode && loading && (
          <div className="KeyTakeaways-loading EditableModule-well">
            <Spinner />
          </div>
        )}
        {editMode && !loading && (
          <textarea
            id="keyTakeawaysTextArea"
            name="keyTakeawaysTextArea"
            className="EditableModule-well"
            aria-label={t('lessons.recap.key_takeaways.title')}
            ref={autosizeRef}
            onChange={e => setKeyTakeawaysText(e.target.value)}
            value={keyTakeawaysText}
          />
        )}

        {!editMode && !keyTakeawaysText && <p>{DEFAULT_TEXT}</p>}
        {!editMode && !!keyTakeawaysText?.length && (
          <ul className="KeyTakeaways--list" data-testid="key-takeaways-list">
            {keyTakeawaysText?.split('\n').map((text, i) => (
              <li key={uniqueId(`${text}`)}>{text}</li>
            ))}
          </ul>
        )}

        {error && (
          <Error
            message={t(
              'lessons.recap.key_takeaways.error_generating_takeaways',
            )}
          />
        )}
      </section>
      {modalVisible && (
        <ConfirmationModal
          closeModal={() => toggleModalVisible(false)}
          discardAndGenerate={discardAndGenerate}
          feature="keyTakeaways"
        />
      )}
    </>
  );
};

export default KeyTakeaways;
